import { Save } from 'lucide-react';
import type { CerFeatures } from '@slims/shared/data';
import { Button } from '../../atoms';
import { StatusBlock } from '../../molecules';

export interface CerFeaturesViewProps {
	name: string;
	features: CerFeatures | null;
	loading: boolean;
	saving: boolean;
	dirty: boolean;
	error: string | null;
	onChange: (features: CerFeatures) => void;
	onSave: () => void;
	onRetry: () => void;
}

/** Capability controls share the CER detail form's typography and surfaces. */
export function CerFeaturesView({
	features,
	loading,
	saving,
	dirty,
	error,
	onChange,
	onSave,
	onRetry,
}: CerFeaturesViewProps) {
	return (
		<div className="space-y-4">
			{loading ? (
				<StatusBlock kind="loading" message="Caricamento funzionalità…" />
			) : !features ? (
				<div className="space-y-4">
					<StatusBlock kind="error" message={error || 'Impossibile caricare le funzionalità.'} />
					<Button variant="outline" onClick={onRetry}>
						Riprova
					</Button>
				</div>
			) : (
				<form
					onSubmit={(event) => {
						event.preventDefault();
						onSave();
					}}
					className="max-w-4xl"
				>
					<fieldset
						disabled={saving}
						className="divide-y divide-border rounded-card border border-border bg-card px-5 sm:px-6"
					>
						<legend className="sr-only">Funzionalità della CER</legend>
						<div className="grid gap-4 py-6 sm:grid-cols-[1fr_14rem] sm:items-start">
							<div>
								<label htmlFor="feature-ai" className="font-semibold text-text">
									Suggerimenti AI
								</label>
								<p id="feature-ai-help" className="mt-2 max-w-prose text-sm text-text-muted">
									Consigli per ottimizzare i consumi e l’energia condivisa. In demo mostriamo esempi
									generati casualmente, indicati come dimostrativi.
								</p>
							</div>
							<select
								id="feature-ai"
								aria-describedby="feature-ai-help"
								value={features.aiSuggestions}
								onChange={(e) =>
									onChange({
										...features,
										aiSuggestions: e.target.value as CerFeatures['aiSuggestions'],
									})
								}
								className="min-h-11 w-full rounded-btn border border-border bg-card px-3 text-sm text-text focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-60"
							>
								<option value="enabled">Attivo</option>
								<option value="disabled">Disattivo</option>
								<option value="demo">Demo</option>
							</select>
						</div>
						{(
							[
								[
									'reports',
									'Report',
									'Accesso ai report, esportazioni e generazione dei report mensili.',
								],
								[
									'anomalies',
									'Rilevamento anomalie',
									'Rilevamento delle irregolarità di consumo e degli impianti, con i relativi avvisi e schermate.',
								],
							] as const
						).map(([key, label, description]) => (
							<div key={key} className="grid gap-4 py-6 sm:grid-cols-[1fr_14rem] sm:items-start">
								<div>
									<label htmlFor={`feature-${key}`} className="font-semibold text-text">
										{label}
									</label>
									<p
										id={`feature-${key}-help`}
										className="mt-2 max-w-prose text-sm text-text-muted"
									>
										{description}
									</p>
								</div>
								<select
									id={`feature-${key}`}
									aria-describedby={`feature-${key}-help`}
									value={features[key] ? 'enabled' : 'disabled'}
									onChange={(e) => onChange({ ...features, [key]: e.target.value === 'enabled' })}
									className="min-h-11 w-full rounded-btn border border-border bg-card px-3 text-sm text-text focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-60"
								>
									<option value="enabled">Attivo</option>
									<option value="disabled">Disattivo</option>
								</select>
							</div>
						))}
					</fieldset>
					<div className="mt-5 flex flex-wrap items-center justify-between gap-4">
						<output className="text-sm text-text-muted">
							{dirty ? 'Modifiche non salvate' : 'Impostazioni salvate'}
						</output>
						<Button type="submit" variant="accent" disabled={saving || !dirty}>
							<Save />
							{saving ? 'Salvataggio…' : 'Salva funzionalità'}
						</Button>
					</div>
					{error && (
						<p role="alert" className="mt-4 text-sm text-danger">
							{error}
						</p>
					)}
				</form>
			)}
		</div>
	);
}
